import { endOfMonth, startOfMonth, subDays, subMonths } from "date-fns";
import React, { useMemo, useState, useEffect } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCSwitch, RCCTooltip } from "react-common-components";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import LovAccount from "src/components/Lov/Account";
import LovCostCenter from "src/components/Lov/CostCenter";
import LovOtherExpenses from "src/components/Lov/OtherAccounts";
import LovProductLine from "src/components/Lov/ProductLine";
import LovSalesChannel from "src/components/Lov/SalesChannel";
import LovSubsidiary from "src/components/Lov/Subsidiary";
import Select from "src/components/Select";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { BudgetType } from "../../interface";
import { IConfigOptions } from "./interface";

const ConfigsOptions: React.FC<IConfigOptions> = ({
	controlFormOptions,
	registerFormOptions,
	setValueFormOptions,
	budgetType,
	budgetPlanId,
	isLoading,
	setSubsidiaryTitle,
	buttonExport,
	yearConsult,
	subsidiary,
	lov1,
	lov2,
	budgetUsesApportionment,
	hasConversionValueEqualZero
}) => {
	const { translate } = useTranslation();
	const [subsidiaryId, setSubsidiary] = useState("");
	const [defaultValueGrouperSelect, setDefaultValueGrouperSelect] = useState<{ value: string; label: string; } | undefined>(budgetType === BudgetType.SALES_REVENUE ? {
		label: translate("Canal de venda"),
		value: "1"
	} : {
		label: translate("Centro de custo"),
		value: "1"
	});

	const GrouperSelect = useMemo(() => {
		if(budgetType === BudgetType.OPERATIONAL_EXPENSES){
			return <Select 
				name="group"
				control={controlFormOptions}
				defaultValue={defaultValueGrouperSelect}
				value={defaultValueGrouperSelect}
				options={[{
					label: translate("Centro de custo"),
					value: "1"
				},{
					label: translate("Conta"),
					value: "2"
				}]}
				label={translate("Agrupador")}
				onChangeCapture={() => setDefaultValueGrouperSelect(undefined)}
			/>;
		}
		if(budgetType === BudgetType.SALES_REVENUE){
			return <Select 
				name="group"
				control={controlFormOptions}
				defaultValue={defaultValueGrouperSelect}
				value={defaultValueGrouperSelect}
				options={[{
					label: translate("Canal de venda"),
					value: "1"
				},{
					label: translate("Produto/Serviço"),
					value: "2"
				}]}
				label={translate("Agrupador")}
				onChangeCapture={() => setDefaultValueGrouperSelect(undefined)}
			/>;
		}

		return <Select 
			name="group"
			control={controlFormOptions}
			defaultValue={defaultValueGrouperSelect}
			value={defaultValueGrouperSelect}
			options={[{
				label: translate("Centro de custo"),
				value: "1"
			},{
				label: translate("Outras contas"),
				value: "2"
			}]}
			label={translate("Agrupador")}
			onChangeCapture={() => setDefaultValueGrouperSelect(undefined)}
		/>;}, [defaultValueGrouperSelect]);

	const FirstLov = useMemo(() => {
		if(budgetType === BudgetType.SALES_REVENUE)
			return <LovSalesChannel
				control={controlFormOptions}
				name="lov1"
				label={translate("Receita de vendas")}
				defaultValue={lov1}
				extraParametersOnLoad={{
					budgetPlanId,
					subsidiaryId
				}}
				setValue={setValueFormOptions}
			/>;
		return(
			<LovCostCenter
				control={controlFormOptions}
				name="lov1"
				label={translate("Centro de custo")}
				defaultValue={lov1}
				extraParametersOnLoad={{
					budgetPlanId,
					subsidiaryId
				}}
				setValue={setValueFormOptions}
			/>
		);}, [subsidiaryId, budgetType]);

	const SecondLov = useMemo(() => {
		if(budgetType === BudgetType.SALES_REVENUE)
			return(
				<LovProductLine
					control={controlFormOptions}
					name="lov2"
					label={translate("Produto/Serviço")}
					defaultValue={lov2}
					extraParametersOnLoad={{
						budgetPlanId
					}}
					setValue={setValueFormOptions}
				/>);
		if(budgetType === BudgetType.OTHER_EXPENSES)
			return(
				<LovOtherExpenses
					control={controlFormOptions}
					name="lov2"
					label={translate("Outras contas")}
					defaultValue={lov2}
					extraParametersOnLoad={{
						budgetPlanId
					}}
					setValue={setValueFormOptions}
				/>
			);
		return(
			<LovAccount
				control={controlFormOptions}
				name="lov2"
				label={translate("Conta")}
				defaultValue={lov2}
				extraParametersOnLoad={{
					budgetPlanId,
					subsidiaryId
				}}
				setValue={setValueFormOptions}
			/>
		);}, [subsidiaryId, budgetType]);

	useEffect(() => {
		setDefaultValueGrouperSelect(budgetType === BudgetType.SALES_REVENUE ? {
			label: translate("Canal de venda"),
			value: "1"
		} : {
			label: translate("Centro de custo"),
			value: "1"
		});
	}, [budgetType]);

	return (
		<>
			<RCCGrid
				container
				item
				spacing={1}
			>
				<RCCGrid
					container 
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					<DateTimePickerRange
						control={controlFormOptions}
						label={translate("Período")}
						labelSeparator={translate("até")}
						nameInit="startDate"
						nameEnd="endDate"
						defaultValueEnd={new Date(yearConsult, 11, 31)}
						defaultValueInit={new Date(yearConsult, 0, 1)}
						type="datePicker"
						setValue={setValueFormOptions}
						periods={[
							{ name:translate("Ano atual") , initialDate: new Date(new Date().getFullYear(), 0, 1), endDate: subDays(new Date(new Date().getFullYear() + 1, 0, 1), 1) },
							{ name:translate("Ano anterior") , initialDate: new Date(new Date().getFullYear() - 1, 0, 1), endDate: subDays(new Date(new Date().getFullYear(), 0, 1), 1) },
							{ name:translate("Mês atual") , initialDate:startOfMonth(new Date()), endDate: endOfMonth(new Date()) },
							{ name:translate("Mês passado") , initialDate:startOfMonth(subMonths(new Date(),1)), endDate: endOfMonth(subMonths(new Date(),1)) }
						]}
						props={{
							datePicker: {
								format: "MM/yyyy",
								views: ["year", "month"]
							}
						}}
					/>
				</RCCGrid>
				<RCCGrid
					container 
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					{GrouperSelect}
				</RCCGrid>
				{budgetType === BudgetType.SALES_REVENUE && (
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<Select 
							name="show"
							control={controlFormOptions}
							defaultValue="R"
							options={[{
								label: translate("Receita"),
								value: "R"
							},{
								label: translate("Quantidade"),
								value: "Q"
							},{
								label: translate("Preço unitário"),
								value: "P"
							}]}
							label={translate("Mostrar")}
						/>
					</RCCGrid>
				)}
				{budgetType === BudgetType.OPERATIONAL_EXPENSES && budgetUsesApportionment && (
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<Select 
							name="showCorporativeOPValues"
							control={controlFormOptions}
							defaultValue="N"
							options={[{
								label: translate("Com rateio"),
								value: "N"
							},{
								label: translate("Sem rateio"),
								value: "S"
							}]}
							label={translate("Valores corporativos")}
						/>
					</RCCGrid>
				)}
			</RCCGrid>

			<RCCGrid
				container
				item
				spacing={1}
			>
				<RCCGrid
					container
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					<LovSubsidiary 
						control={controlFormOptions}
						name="subsidiary"
						label={translate("Unidade/Filial")}
						defaultValue={subsidiary}
						extraParametersOnLoad={{
							budgetPlanId
						}}
						onChangeCapture={(e: any) => {setSubsidiaryTitle(e.label); setSubsidiary(e.value);}}
						setValue={setValueFormOptions}
					/>
				</RCCGrid>
				<RCCGrid
					container
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					{FirstLov}
				</RCCGrid>
				<RCCGrid
					container
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					{SecondLov}
				</RCCGrid>
			</RCCGrid>

			<RCCGrid
				container
				item
				spacing={1}
			>
				<RCCGrid
					container 
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					<RCCSwitch 
						name="showLevelBelow" 
						label={translate("Mostrar nível inferior do agrupador")} 
						register={registerFormOptions} 
						labelPosition="end"
					/>
				</RCCGrid>
				<RCCGrid
					container 
					item
					xs={12}
					sm={6}
					md={4}
					lg={4}
				>
					<RCCSwitch 
						name="showHierarchy" 
						label={translate("Mostrar hierarquia")} 
						register={registerFormOptions} 
						labelPosition="end"
					/>
				</RCCGrid>
			</RCCGrid>

			<RCCGrid container item spacing={1} justifyContent="flex-end">
				{hasConversionValueEqualZero && (
					<RCCGrid container item alignItems="center" style={{ flex: 1 }}>
						<RCCIcon name="exclamationTriangle" color="var(--yellow)" />
						<Title color="defaultColor" size="xs">
							{translate("Existem períodos em que não foi cadastrado a conversão da moeda")}
						</Title>
					</RCCGrid>
				)}
				{buttonExport}
				<RCCTooltip
					text={translate("Consultar")}
					color="purple"
				>
					<RCCButton
						color="purple"
						icon={{
							name: "search"
						}}
						layout="isRounded"
						size="md"
						type="submit"
						loadingText=""
						loading={isLoading}
					/>
				</RCCTooltip>
			</RCCGrid>
		</>
	);
};

export default ConfigsOptions;
